/**
 * SudoPasswordPrompt — the web app's one password prompt, mounted once
 * beside the app. An action the backend keeps behind the sudo window (making
 * a vendor API key) answers 403 with X-Sudo-Required; the axios interceptor
 * asks this prompt through @gearghost/sudo-prompt and retries the request
 * once the password is taken. Screens carry no password field of their own.
 *
 * The answer is true once the server takes the password (the window is then
 * open for five minutes), false on Cancel. A wrong password keeps the prompt
 * open with the server's message; the account's sign-in lockout counts it.
 */
import { useEffect, useRef, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { registerPromptHandler, unregisterPromptHandler } from '@gearghost/sudo-prompt'
import { UserSudoRequestSchema } from '@gearghost/api-schemas/auth'
import { zodToFieldErrors } from '@gearghost/api-schemas/formValidation'
import { useEnterSudoMutation } from '@/redux/api/authApi'
import { getErrorMessage } from '@/utils/errors'

const FIELDS = ['password'] as const

export default function SudoPasswordPrompt() {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const answerRef = useRef<((granted: boolean) => void) | null>(null)
  const [enterSudo, { isLoading }] = useEnterSudoMutation()

  useEffect(() => {
    registerPromptHandler(
      () =>
        new Promise<boolean>((resolve) => {
          answerRef.current = resolve
          setPassword('')
          setFieldError(null)
          setRefusal(null)
          setOpen(true)
        }),
    )
    return () => unregisterPromptHandler()
  }, [])

  const answer = (granted: boolean) => {
    setOpen(false)
    setPassword('')
    answerRef.current?.(granted)
    answerRef.current = null
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setRefusal(null)
    const errors = zodToFieldErrors(UserSudoRequestSchema, { password }, FIELDS)
    setFieldError(errors?.password ?? null)
    if (errors) return
    try {
      await enterSudo({ password }).unwrap()
      answer(true)
    } catch (err) {
      setRefusal(getErrorMessage(err, 'Your password could not be checked. Try again.'))
    }
  }

  return (
    <Modal show={open} onHide={() => answer(false)} centered className='modal_css modal_css1'>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          <div className='modal_Inner'>
            <h1 className='mb-2'>{"Confirm it's you"}</h1>
            <p className='para'>Enter your password to continue.</p>
            <Form.Group className='text-start mt-3'>
              <Form.Label htmlFor='sudo-password'>Password</Form.Label>
              <Form.Control
                id='sudo-password'
                data-testid='sudo-password'
                type='password'
                autoComplete='current-password'
                autoFocus
                value={password}
                aria-invalid={fieldError ? 'true' : 'false'}
                onChange={(e) => setPassword(e.target.value)}
              />
              {fieldError && <div className='error-message'>{fieldError}</div>}
            </Form.Group>
            {refusal && (
              <p role='alert' className='error-message mt-2'>
                {refusal}
              </p>
            )}
          </div>
          <div className='modal_footer'>
            <Button variant='outline-primary' onClick={() => answer(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={isLoading}>
              Continue
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  )
}
